// Clients, client aliases and seniority (PRD rev. 1.19, P2M-a).
//
// ClientAliasesPanel is the one component behind CA2: the person card shows it for a person, every
// project card shows it for that project's client, and both read and write the same alias records,
// so an edit on any card is an edit everywhere.

import { BillingCard } from "./Expenses";
import { useState, type ReactNode } from "react";
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Textarea,
} from "@fluentui/react-components";
import {
  ALIAS_STATUS_LABEL,
  ALIAS_STATUSES,
  clientIdOf,
  CONTACT_PURPOSE_LABEL,
  CONTACT_PURPOSES,
  currentSince,
  invoiceDayLabel,
  REVENUE_TYPE_LABEL,
  REVENUE_TYPES,
  today,
  type ClientBilling,
  type InvoiceRule,
  type RevenueType,
  type AliasFlag,
  type AliasStatus,
  type ClientAlias,
  type ClientContact,
  type ContactPurpose,
} from "../../../core";
import { errorText, send, useApi } from "../api";
import { DateInput } from "../DateInput";
import { InvoiceDaySelect } from "../InvoiceDaySelect";
import { day } from "../format";
import { href } from "../router";
import { ErrorBar, Loading, Page, useMe } from "../ui";

// --- API shapes -------------------------------------------------------------------------------------

type AliasRow = ClientAlias & { _etag: string; personName: string; personStatus?: string; clientName: string };
interface AliasesResponse {
  aliases: AliasRow[];
  flags: AliasFlag[];
  access: { edit: boolean };
}
interface ClientsResponse {
  clients: { id: string; name: string; projects: { id: string; code: string; name: string; status: string; billable?: boolean }[]; contacts?: number; aliasAdmins?: number; aliases?: number }[];
}
interface ClientResponse {
  client: { id: string; name: string; notes?: string; etag: string | null; saved: boolean };
  projects: { id: string; code: string; name: string; status: string }[];
  contacts?: ClientContact[];
  aliases?: AliasRow[];
  billing?: ClientBilling & { set: boolean };
  access: { aliases: boolean; edit: boolean; billing: boolean };
}
interface SeniorityResponse {
  order: { id: string; name: string }[];
  saved: boolean;
  etag: string | null;
  edit: boolean;
}

type Close = (changed: boolean) => void;

// --- Small shared pieces ------------------------------------------------------------------------------

function Frame(props: { title: string; children: ReactNode; error?: string; action: string; busy: boolean; disabled?: boolean; onAction: () => void; onClose: Close }) {
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && props.onClose(false)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{props.title}</DialogTitle>
          <DialogContent className="dialog-stack">{props.children}</DialogContent>
          <ErrorBar message={props.error} />
          <DialogActions>
            <Button appearance="secondary" onClick={() => props.onClose(false)}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={props.busy || props.disabled} onClick={props.onAction}>
              {props.busy ? "Saving…" : props.action}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function useSave(onClose: Close) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
      onClose(true);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  return { busy, error, run };
}

const STATUS_COLOR: Record<AliasStatus, "informative" | "success" | "warning" | "subtle"> = {
  requested: "informative",
  active: "success",
  "disable-requested": "warning",
  disabled: "subtle",
};

export function AliasStatusBadge({ status }: { status: AliasStatus }) {
  return (
    <Badge appearance="tint" color={STATUS_COLOR[status]}>
      {ALIAS_STATUS_LABEL[status]}
    </Badge>
  );
}

// --- CA2: the panel on person and project cards ----------------------------------------------------------

type Open = { kind: "add" } | { kind: "status"; alias: AliasRow } | { kind: "edit"; alias: AliasRow } | { kind: "remove"; alias: AliasRow } | null;

/**
 * Client aliases for one person (on their card) or one client (on every project card of that client).
 * Renders nothing for someone without aliases.read.
 */
export function ClientAliasesPanel(props: { personId?: string; personName?: string; clientName?: string }) {
  const me = useMe();
  const [open, setOpen] = useState<Open>(null);
  const clientId = props.clientName ? clientIdOf(props.clientName) : "";
  const query = props.personId ? `personId=${encodeURIComponent(props.personId)}` : clientId ? `clientId=${encodeURIComponent(clientId)}` : null;
  const data = useApi<AliasesResponse>(me.can("aliases.read") && query ? `/aliases?${query}` : null);
  if (!me.can("aliases.read") || !query) return null;
  const forPerson = !!props.personId;
  const close = (changed: boolean) => {
    setOpen(null);
    if (changed) data.reload();
  };
  const rows = data.data?.aliases ?? [];
  const edit = data.data?.access.edit ?? false;
  return (
    <Card className="panel">
      <div className="panel-head">
        <Subtitle2>Client aliases</Subtitle2>
        {edit && (
          <Button size="small" onClick={() => setOpen({ kind: "add" })}>
            Add alias
          </Button>
        )}
      </div>
      <Caption1>
        {forPerson
          ? "Accounts this person holds in clients' tenants. The same records show on every project of each client."
          : <>Accounts 3Seven people hold at {props.clientName}. The same records show on every {props.clientName} project and on each person's card. <a href={href("clients", clientId)}>Client page</a></>}
      </Caption1>
      <ErrorBar message={data.error} />
      {data.loading && !data.data ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Body1>No client aliases on file.</Body1>
      ) : (
        <AliasTable rows={rows} show={forPerson ? "client" : "person"} edit={edit} onOpen={setOpen} />
      )}
      {open?.kind === "add" && <AliasDialog personId={props.personId} clientName={props.clientName} onClose={close} />}
      {open?.kind === "edit" && <AliasDialog alias={open.alias} onClose={close} />}
      {open?.kind === "status" && <StatusDialog alias={open.alias} onClose={close} />}
      {open?.kind === "remove" && <RemoveDialog alias={open.alias} onClose={close} />}
    </Card>
  );
}

function AliasTable({ rows, show, edit, onOpen, flagged }: { rows: AliasRow[]; show: "client" | "person" | "both"; edit: boolean; onOpen: (o: Open) => void; flagged?: Set<string> }) {
  return (
    <Table size="small" aria-label="Client aliases">
      <TableHeader>
        <TableRow>
          {show !== "client" && <TableHeaderCell>Person</TableHeaderCell>}
          {show !== "person" && <TableHeaderCell>Client</TableHeaderCell>}
          <TableHeaderCell>Alias</TableHeaderCell>
          <TableHeaderCell>Status</TableHeaderCell>
          <TableHeaderCell>Since</TableHeaderCell>
          <TableHeaderCell>History</TableHeaderCell>
          {edit && <TableHeaderCell aria-label="Actions" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((a) => (
          <TableRow key={a.id} className={flagged?.has(a.id) ? "row-flag" : undefined}>
            {show !== "client" && (
              <TableCell>
                <a href={href("people", a.personId)}>{a.personName}</a>
                {a.personStatus === "archived" && (
                  <Badge appearance="outline" size="small" className="inline-badge">
                    Archived
                  </Badge>
                )}
              </TableCell>
            )}
            {show !== "person" && (
              <TableCell>
                <a href={href("clients", a.clientId)}>{a.clientName}</a>
              </TableCell>
            )}
            <TableCell>{a.address}</TableCell>
            <TableCell>
              <AliasStatusBadge status={a.status} />
            </TableCell>
            <TableCell>{day(currentSince(a))}</TableCell>
            <TableCell>
              <Caption1>{a.history.map((h) => `${ALIAS_STATUS_LABEL[h.status]} ${day(h.on)}`).join(" · ")}</Caption1>
            </TableCell>
            {edit && (
              <TableCell>
                <div className="row-actions">
                  <Button size="small" onClick={() => onOpen({ kind: "status", alias: a })}>
                    Change status…
                  </Button>
                  <Button size="small" appearance="subtle" onClick={() => onOpen({ kind: "edit", alias: a })}>
                    Edit
                  </Button>
                  <Button size="small" appearance="subtle" onClick={() => onOpen({ kind: "remove", alias: a })}>
                    Remove
                  </Button>
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Add an alias, or edit its address and note. Status changes go through StatusDialog, dated. */
function AliasDialog(props: { alias?: AliasRow; personId?: string; clientName?: string; onClose: Close }) {
  const editing = !!props.alias;
  const people = useApi<{ people: { id: string; name: string; status: string }[] }>(editing ? null : "/people?view=names");
  const clients = useApi<ClientsResponse>(editing ? null : "/clients");
  const [personId, setPersonId] = useState(props.alias?.personId ?? props.personId ?? "");
  const [clientName, setClientName] = useState(props.alias?.clientName ?? props.clientName ?? "");
  const [address, setAddress] = useState(props.alias?.address ?? "");
  const [status, setStatus] = useState<AliasStatus>("active");
  const [on, setOn] = useState(today());
  const [note, setNote] = useState(props.alias?.note ?? "");
  const save = useSave(props.onClose);
  const submit = () =>
    save.run(() =>
      editing
        ? send("PATCH", `/aliases/${encodeURIComponent(props.alias!.id)}`, { etag: props.alias!._etag, address, aliasNote: note })
        : send("POST", "/aliases", { personId, clientName, address, status, on, aliasNote: note }),
    );
  return (
    <Frame title={editing ? `Edit ${props.alias!.address}` : "Add a client alias"} action={editing ? "Save" : "Add alias"} busy={save.busy} disabled={!address.trim() || !personId || !clientName.trim()} error={save.error} onClose={props.onClose} onAction={() => void submit()}>
      {!editing && (
        <>
          <Field label="Person" required>
            <Select value={personId} disabled={!!props.personId} onChange={(_, d) => setPersonId(d.value)}>
              <option value="">Pick a person…</option>
              {(people.data?.people ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.status === "archived" ? " (archived)" : ""}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Client" required hint="Pick from the list, or type a client's name.">
            <Input list="alias-clients" value={clientName} disabled={!!props.clientName} onChange={(_, d) => setClientName(d.value)} />
            <datalist id="alias-clients">
              {(clients.data?.clients ?? []).map((c) => (
                <option key={c.id} value={c.name} />
              ))}
            </datalist>
          </Field>
        </>
      )}
      <Field label="Alias address" required hint="The account in the client's tenant, e.g. name@client.com.">
        <Input type="email" value={address} onChange={(_, d) => setAddress(d.value)} />
      </Field>
      {!editing && (
        <div className="form-grid two">
          <Field label="Status" hint="Aliases are obtained through onboarding with the client; Base Camp only tracks them.">
            <Select value={status} onChange={(_, d) => setStatus(d.value as AliasStatus)}>
              <option value="requested">{ALIAS_STATUS_LABEL.requested}</option>
              <option value="active">{ALIAS_STATUS_LABEL.active}</option>
            </Select>
          </Field>
          <Field label="Since" required>
            <DateInput type="date" value={on} max={today()} onChange={(_, d) => setOn(d.value)} />
          </Field>
        </div>
      )}
      <Field label="Note">
        <Textarea value={note} onChange={(_, d) => setNote(d.value)} resize="vertical" />
      </Field>
    </Frame>
  );
}

/** A dated status change (CA1). The date is when it happened, which can be before today. */
function StatusDialog({ alias, onClose }: { alias: AliasRow; onClose: Close }) {
  const [status, setStatus] = useState<AliasStatus>(alias.status === "active" ? "disable-requested" : alias.status === "disable-requested" ? "disabled" : alias.status === "requested" ? "active" : alias.status);
  const [on, setOn] = useState(today());
  const [note, setNote] = useState("");
  const save = useSave(onClose);
  const last = alias.history[alias.history.length - 1];
  return (
    <Frame
      title={`Change status: ${alias.address}`}
      action="Record change"
      busy={save.busy}
      disabled={!on || (status === alias.status && !note.trim())}
      error={save.error}
      onClose={onClose}
      onAction={() => void save.run(() => send("PATCH", `/aliases/${encodeURIComponent(alias.id)}`, { etag: alias._etag, status, on, note }))}
    >
      <Body1>
        Now <AliasStatusBadge status={alias.status} /> since {day(last?.on)}. Record the day it actually happened: when the request went to the client, or when they confirmed.
      </Body1>
      <div className="form-grid two">
        <Field label="New status">
          <Select value={status} onChange={(_, d) => setStatus(d.value as AliasStatus)}>
            {ALIAS_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ALIAS_STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="On" required>
          <DateInput type="date" value={on} min={last?.on} max={today()} onChange={(_, d) => setOn(d.value)} />
        </Field>
      </div>
      <Field label="Note" hint={status === "disabled" ? "e.g. who confirmed it, and how." : undefined}>
        <Textarea value={note} onChange={(_, d) => setNote(d.value)} resize="vertical" />
      </Field>
    </Frame>
  );
}

function RemoveDialog({ alias, onClose }: { alias: AliasRow; onClose: Close }) {
  const [reason, setReason] = useState("");
  const save = useSave(onClose);
  return (
    <Frame title={`Remove ${alias.address}`} action="Remove" busy={save.busy} disabled={!reason.trim()} error={save.error} onClose={onClose} onAction={() => void save.run(() => send("POST", `/aliases/${encodeURIComponent(alias.id)}/delete`, { etag: alias._etag, reason }))}>
      <Body1>Remove an alias only if it was entered in error. For an account the client switched off, change its status to Disabled instead, so the dates are kept.</Body1>
      <Field label="Reason" required>
        <Textarea value={reason} onChange={(_, d) => setReason(d.value)} placeholder="e.g. typed the wrong client" />
      </Field>
    </Frame>
  );
}

// --- Clients screen ------------------------------------------------------------------------------------

export function Clients({ id }: { id?: string }) {
  return id ? <ClientPage id={id} /> : <ClientsList />;
}

/** A client is billable when any of its projects is, and active while any project is still open (Chris, 2026-10-15). */
const CLOSED = new Set(["delivered", "lost", "cancelled"]);
const clientBillable = (c: ClientsResponse["clients"][number]) => c.projects.some((p) => p.billable);
const clientActive = (c: ClientsResponse["clients"][number]) => c.projects.some((p) => !CLOSED.has(p.status));

function ClientsList() {
  const data = useApi<ClientsResponse>("/clients");
  const me = useMe();
  const seeAliases = me.can("aliases.read");
  const [billable, setBillable] = useState<"" | "billable" | "non-billable">("");
  const [status, setStatus] = useState<"" | "active" | "inactive">("active");
  const rows = (data.data?.clients ?? []).filter(
    (c) => (!billable || clientBillable(c) === (billable === "billable")) && (!status || clientActive(c) === (status === "active")),
  );
  return (
    <Page title="Clients">
      <div className="filters">
        <Field label="Billable">
          <Select value={billable} onChange={(_, d) => setBillable(d.value as typeof billable)}>
            <option value="">All</option>
            <option value="billable">Billable</option>
            <option value="non-billable">Non-billable</option>
          </Select>
        </Field>
        <Field label="Status">
          <Select value={status} onChange={(_, d) => setStatus(d.value as typeof status)}>
            <option value="">All</option>
            <option value="active">Active: an open project</option>
            <option value="inactive">Inactive: every project closed</option>
          </Select>
        </Field>
      </div>
      <ErrorBar message={data.error} />
      {!data.data ? (
        <Loading />
      ) : (
        <Card className="panel">
          <Table size="small" aria-label="Clients">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Client</TableHeaderCell>
                <TableHeaderCell>Projects</TableHeaderCell>
                <TableHeaderCell>Billable</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                {seeAliases && <TableHeaderCell>Alias admins</TableHeaderCell>}
                {seeAliases && <TableHeaderCell>Live aliases</TableHeaderCell>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <a href={href("clients", c.id)}>{c.name}</a>
                  </TableCell>
                  <TableCell>{c.projects.map((p) => p.code).join(", ") || "—"}</TableCell>
                  <TableCell>{clientBillable(c) ? "Billable" : "Non-billable"}</TableCell>
                  <TableCell>{clientActive(c) ? "Active" : "Inactive"}</TableCell>
                  {seeAliases && <TableCell>{c.aliases && !c.aliasAdmins ? <Badge appearance="tint" color="warning">None on file</Badge> : c.aliasAdmins ?? 0}</TableCell>}
                  {seeAliases && <TableCell>{c.aliases ?? 0}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!rows.length && <Body1 className="muted">No clients match these filters.</Body1>}
        </Card>
      )}
    </Page>
  );
}

function ClientPage({ id }: { id: string }) {
  const data = useApi<ClientResponse>(`/clients/${encodeURIComponent(id)}`);
  const d = data.data;
  return (
    <Page title={d?.client.name ?? "Client"}>
      <div className="page-nav">
        <a href={href("clients")} className="back">
          ← Clients
        </a>
      </div>
      <ErrorBar message={data.error} />
      {!d ? (
        <Loading />
      ) : (
        <>
          <Card className="panel">
            <Subtitle2>Projects</Subtitle2>
            {d.projects.length ? (
              <ul className="plain-list">
                {d.projects.map((p) => (
                  <li key={p.id}>
                    <a href={href("projects", p.id)}>
                      {p.code} · {p.name}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <Body1>No projects you can see.</Body1>
            )}
          </Card>
          {d.billing && <PaymentTermsPanel key={d.client.etag ?? "new"} data={d} reload={data.reload} />}
          {d.access.aliases && <ContactsPanel key={`c-${d.client.etag ?? "new"}`} data={d} reload={data.reload} />}
          <ClientAliasesPanel clientName={d.client.name} />
          <BillingCard client={d.client.name} />
        </>
      )}
    </Page>
  );
}

/**
 * CF1–CF2: the client's payment terms and invoice table. 3Seven bills in advance; a service's invoice
 * goes out on the contract date's day unless its row here says otherwise, and any payment line on a
 * project can still set its own day.
 */
function PaymentTermsPanel({ data, reload }: { data: ClientResponse; reload: () => void }) {
  const saved = data.billing!;
  const [terms, setTerms] = useState(String(saved.termsDays));
  const [rules, setRules] = useState<InvoiceRule[]>(saved.rules);
  const [notes, setNotes] = useState(saved.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const edit = data.access.billing;
  const dirty = terms !== String(saved.termsDays) || JSON.stringify(rules) !== JSON.stringify(saved.rules) || notes !== (saved.notes ?? "");
  const unused = REVENUE_TYPES.filter((t) => !rules.some((r) => r.type === t));
  const setRule = (i: number, patch: Partial<InvoiceRule>) => setRules(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", `/clients/${encodeURIComponent(data.client.id)}/billing`, { etag: data.client.etag, termsDays: Number(terms), rules, notes });
      reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="panel">
      <div className="panel-head">
        <Subtitle2>Payment terms and invoicing</Subtitle2>
        {edit && (
          <div className="row-actions">
            {unused.length > 0 && (
              <Button size="small" onClick={() => setRules([...rules, { type: unused[0], day: "contract" }])}>
                Add a service
              </Button>
            )}
            <Button size="small" appearance="primary" disabled={!dirty || busy || terms === ""} onClick={() => void save()}>
              {busy ? "Saving…" : "Save terms"}
            </Button>
          </div>
        )}
      </div>
      <Caption1>
        Invoices go out in advance, on the day of the month each project's contract is dated, unless a service below has its own day. A payment line on a project can still set
        its own. The cash forecast expects payment the number of days below after each invoice.
        {!saved.set && " Not set yet: Net 45 is the default."}
      </Caption1>
      <ErrorBar message={error} />
      <div className="form-grid three">
        <Field label="Payment terms" hint="Days from invoice to payment.">
          <Input type="number" min={0} max={180} contentBefore="Net" value={terms} disabled={!edit} onChange={(_, d) => setTerms(d.value)} aria-label="Net days" />
        </Field>
      </div>
      {rules.length === 0 ? (
        <Body1 className="muted">Every service invoices on the contract date's day.</Body1>
      ) : (
        <Table size="small" aria-label="Invoice table">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Service</TableHeaderCell>
              <TableHeaderCell>Invoice day</TableHeaderCell>
              <TableHeaderCell>Note</TableHeaderCell>
              {edit && <TableHeaderCell aria-label="Remove" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.map((r, i) => (
              <TableRow key={r.type}>
                <TableCell>
                  {edit ? (
                    <Select aria-label="Service" value={r.type} onChange={(_, d) => setRule(i, { type: d.value as RevenueType })}>
                      {REVENUE_TYPES.filter((t) => t === r.type || unused.includes(t)).map((t) => (
                        <option key={t} value={t}>
                          {REVENUE_TYPE_LABEL[t]}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    REVENUE_TYPE_LABEL[r.type]
                  )}
                </TableCell>
                <TableCell>{edit ? <InvoiceDaySelect value={r.day} onChange={(v) => v !== undefined && setRule(i, { day: v })} /> : invoiceDayLabel(r.day)}</TableCell>
                <TableCell>{edit ? <Input aria-label="Note" value={r.note ?? ""} placeholder="e.g. portal cut-off" onChange={(_, d) => setRule(i, { note: d.value || undefined })} /> : r.note}</TableCell>
                {edit && (
                  <TableCell>
                    <Button size="small" appearance="subtle" onClick={() => setRules(rules.filter((_, j) => j !== i))}>
                      Remove
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Field label="Billing notes" hint="Anything else agreed with the client: portal, PO numbers, who receives invoices.">
        <Textarea value={notes} disabled={!edit} onChange={(_, d) => setNotes(d.value)} resize="vertical" />
      </Field>
    </Card>
  );
}

/** CA3: who at the client handles what. Disable requests go to the alias admins. */
function ContactsPanel({ data, reload }: { data: ClientResponse; reload: () => void }) {
  const [rows, setRows] = useState<ClientContact[]>(data.contacts ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const edit = data.access.edit;
  const dirty = JSON.stringify(rows) !== JSON.stringify(data.contacts ?? []);
  const setRow = (i: number, patch: Partial<ClientContact>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", `/clients/${encodeURIComponent(data.client.id)}`, { etag: data.client.etag, name: data.client.name, contacts: rows, notes: data.client.notes });
      reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const admins = rows.filter((r) => r.purpose === "alias-admin").length;
  return (
    <Card className="panel">
      <div className="panel-head">
        <Subtitle2>Contacts</Subtitle2>
        {edit && (
          <div className="row-actions">
            <Button size="small" onClick={() => setRows([...rows, { id: crypto.randomUUID(), name: "", email: "", purpose: "alias-admin" }])}>
              Add contact
            </Button>
            <Button size="small" appearance="primary" disabled={!dirty || busy} onClick={() => void save()}>
              {busy ? "Saving…" : "Save contacts"}
            </Button>
          </div>
        )}
      </div>
      <Caption1>When someone with an alias here leaves, the request to switch it off goes to the alias admins.</Caption1>
      {admins === 0 && (
        <MessageBar intent="warning">
          <MessageBarBody>No alias admin on file for {data.client.name}.</MessageBarBody>
        </MessageBar>
      )}
      <ErrorBar message={error} />
      {rows.length === 0 ? (
        <Body1>No contacts yet.</Body1>
      ) : (
        <Table size="small" aria-label="Contacts">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>Purpose</TableHeaderCell>
              {edit && <TableHeaderCell aria-label="Remove" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.id}>
                <TableCell>{edit ? <Input aria-label="Name" value={r.name} onChange={(_, d) => setRow(i, { name: d.value })} /> : r.name}</TableCell>
                <TableCell>{edit ? <Input aria-label="Email" type="email" value={r.email} onChange={(_, d) => setRow(i, { email: d.value })} /> : r.email}</TableCell>
                <TableCell>
                  {edit ? (
                    <Select aria-label="Purpose" value={r.purpose} onChange={(_, d) => setRow(i, { purpose: d.value as ContactPurpose })}>
                      {CONTACT_PURPOSES.map((p) => (
                        <option key={p} value={p}>
                          {CONTACT_PURPOSE_LABEL[p]}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    CONTACT_PURPOSE_LABEL[r.purpose]
                  )}
                </TableCell>
                {edit && (
                  <TableCell>
                    <Button size="small" appearance="subtle" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                      Remove
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

// --- CA7: the Aliases screen -----------------------------------------------------------------------------

export function Aliases() {
  const data = useApi<AliasesResponse>("/aliases");
  const [open, setOpen] = useState<Open>(null);
  const [status, setStatus] = useState<"" | AliasStatus>("");
  const [text, setText] = useState("");
  const close = (changed: boolean) => {
    setOpen(null);
    if (changed) data.reload();
  };
  const flagged = new Set((data.data?.flags ?? []).map((f) => f.aliasId));
  const q = text.trim().toLowerCase();
  const rows = (data.data?.aliases ?? [])
    .filter((a) => (!status || a.status === status) && (!q || `${a.personName} ${a.clientName} ${a.address}`.toLowerCase().includes(q)))
    // What needs attention first, then by person and client.
    .sort((a, b) => Number(flagged.has(b.id)) - Number(flagged.has(a.id)));
  return (
    <Page title="Client aliases" actions={data.data?.access.edit && <Button onClick={() => setOpen({ kind: "add" })}>Add alias</Button>}>
      <ErrorBar message={data.error} />
      {(data.data?.flags.length ?? 0) > 0 && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <ul className="plain-list">
              {data.data!.flags.map((f) => (
                <li key={`${f.aliasId}-${f.kind}`}>{f.text}</li>
              ))}
            </ul>
          </MessageBarBody>
        </MessageBar>
      )}
      <div className="filters">
        <Field label="Status">
          <Select value={status} onChange={(_, d) => setStatus(d.value as "" | AliasStatus)}>
            <option value="">Any</option>
            {ALIAS_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ALIAS_STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Search">
          <Input value={text} placeholder="Person, client or address" onChange={(_, d) => setText(d.value)} />
        </Field>
      </div>
      {!data.data ? (
        <Loading />
      ) : (
        <Card className="panel">{rows.length ? <AliasTable rows={rows} show="both" edit={data.data.access.edit} onOpen={setOpen} flagged={flagged} /> : <Body1>No client aliases match.</Body1>}</Card>
      )}
      {open?.kind === "add" && <AliasDialog onClose={close} />}
      {open?.kind === "edit" && <AliasDialog alias={open.alias} onClose={close} />}
      {open?.kind === "status" && <StatusDialog alias={open.alias} onClose={close} />}
      {open?.kind === "remove" && <RemoveDialog alias={open.alias} onClose={close} />}
    </Page>
  );
}

// --- MO9: seniority, on Settings ---------------------------------------------------------------------------

export function Seniority() {
  const data = useApi<SeniorityResponse>("/seniority");
  const [order, setOrder] = useState<{ id: string; name: string }[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  if (!data.data) return data.error ? <ErrorBar message={data.error} /> : <Loading />;
  const list = order ?? data.data.order;
  const move = (i: number, by: number) => {
    const next = [...list];
    const [x] = next.splice(i, 1);
    next.splice(i + by, 0, x);
    setOrder(next);
  };
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/seniority", { etag: data.data!.etag, order: list.map((p) => p.id) });
      setOrder(null);
      data.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="panel">
      <Subtitle2>Seniority</Subtitle2>
      <Caption1>
        Most senior first. When a client organizes a meeting and the project's owner wasn't in it, the most senior 3Seven person who attended is responsible for its transcript.
        {!data.data.saved && " Not saved yet: this is the starting order (Chris, Brent, Brian, then by start date)."}
      </Caption1>
      <ErrorBar message={error} />
      <ol className="seniority-list">
        {list.map((p, i) => (
          <li key={p.id}>
            <span>{p.name}</span>
            {data.data!.edit && (
              <span className="row-actions">
                <Button size="small" appearance="subtle" disabled={i === 0} aria-label={`Move ${p.name} up`} onClick={() => move(i, -1)}>
                  ↑
                </Button>
                <Button size="small" appearance="subtle" disabled={i === list.length - 1} aria-label={`Move ${p.name} down`} onClick={() => move(i, 1)}>
                  ↓
                </Button>
              </span>
            )}
          </li>
        ))}
      </ol>
      {data.data.edit && (
        <div className="row-actions">
          <Button appearance="primary" disabled={busy || (order === null && data.data.saved)} onClick={() => void save()}>
            {busy ? "Saving…" : "Save order"}
          </Button>
          {order && (
            <Button appearance="subtle" onClick={() => setOrder(null)}>
              Undo changes
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
